import React from 'react';
import { numberToCurrencyString } from 'further-ui/utils';
import {
  LpFeeCommitmentStatusLabels,
  LpFeeCalculationBasisLabels,
} from 'further-ui/labels';
import {
  LpFeeCalculationBasis,
  LpFeeCommitmentStatus,
} from 'further-types/lp/lp-fee-template';
import { CalendarDay } from 'further-ui/utils';
import { sumBy } from 'lodash';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { Column, Row } from 'further-ui/components/Layout';
import Statistic from 'components/new/Statistic';
import Divider from '@mui/material/Divider';
import { Typography } from '@mui/material';
import AvatarWithName from 'components/new/AvatarWithName';

type FeeData = {
  _id: string;
  limitedPartner: string;
  accrualDate: CalendarDay;
  lpFeeTemplateName: string;
  calculationBasis: LpFeeCalculationBasis;
  lpFeeTemplateFeeName: string;
  insideOutsideCommitment: LpFeeCommitmentStatus;
  feeExTaxLcy: number;
  salesTaxAmountLcy: number;
  feeIncTaxLcy: number;
};

interface FeesSummaryDisplayProps {
  feeData: Array<FeeData>;
  feesDate: CalendarDay;
  currency?: CurrencyCode;
}

const FeesSummaryDisplay: React.FC<FeesSummaryDisplayProps> = ({
  feeData,
  feesDate,
  currency,
}) => {
  const feeCount = feeData.length;
  const totalIncTax = sumBy(feeData, 'feeIncTaxLcy');

  const tableRows = feeData.map((fee) => ({
    ...fee,
    id: fee._id,
    limitedPartner: fee.limitedPartner,
    lpFeeTemplateName: fee.lpFeeTemplateName,
    lpFeeTemplateFeeName: fee.lpFeeTemplateFeeName,
    insideOutsideCommitment: fee.insideOutsideCommitment,
  }));

  const columns: Array<TableColumn<(typeof tableRows)[number]>> = [
    {
      label: 'Limited partner',
      render: ({ limitedPartner }) => <AvatarWithName name={limitedPartner} />,
      width: 300,
      key: 'limitedPartner',
    },
    {
      label: 'Accrual date',
      key: 'accrualDate',
      width: 150,
      render: ({ accrualDate }) => accrualDate.toReadable(),
    },
    {
      label: 'Template name',
      key: 'lpFeeTemplateName',
      width: 180,
    },
    {
      label: 'Fee name',
      key: 'lpFeeTemplateFeeName',
      width: 180,
    },
    {
      label: 'Inside/outside commitment',
      key: 'insideOutsideCommitment',
      width: 180,
      render: ({ insideOutsideCommitment }) =>
        LpFeeCommitmentStatusLabels.get(insideOutsideCommitment),
    },
    {
      label: 'Calculation basis',
      key: 'calculationBasis',
      width: 180,
      render: ({ calculationBasis }) =>
        LpFeeCalculationBasisLabels.get(calculationBasis),
    },
    {
      label: 'Fee',
      key: 'feeExTaxLcy',
      width: 180,
      render: ({ feeExTaxLcy }) =>
        numberToCurrencyString(feeExTaxLcy, { currency }),
    },
    {
      label: 'Sales tax',
      key: 'salesTaxAmountLcy',
      width: 180,
      render: ({ salesTaxAmountLcy }) =>
        numberToCurrencyString(salesTaxAmountLcy, { currency }),
    },
    {
      label: 'Total charge',
      key: 'feeIncTaxLcy',
      width: 180,
      render: ({ feeIncTaxLcy }) =>
        numberToCurrencyString(feeIncTaxLcy, { currency }),
    },
  ];

  return (
    <Column spacing="md">
      <Row spacing="md">
        <Statistic
          paddingY="sm"
          paddingX="sm"
          spacing="sm"
          label="Number of fees"
          value={feeCount ?? <>0</>}
        />
        <Divider orientation="vertical" />
        <Statistic
          paddingY="sm"
          paddingX="xs"
          spacing="sm"
          label="Fees total"
          value={numberToCurrencyString(totalIncTax, { currency })}
        />
        <Divider orientation="vertical" />
        <Statistic
          paddingY="sm"
          paddingX="xs"
          spacing="sm"
          label="Fees date"
          value={feesDate.toReadable()}
        />
      </Row>
      <Divider />
      <Column spacing="sm">
        <Typography variant="h5">Summary of fees being processed</Typography>
        <Table
          emptyMessage="No fees to display"
          rows={tableRows}
          columns={columns}
          scrollable
        />
      </Column>
    </Column>
  );
};

export default FeesSummaryDisplay;
